import { BusIcon } from '@phosphor-icons/react';
import { fmtTime, fmtWeekdayDate } from '../../../flight/results/flight_format.js';
import { useCheckoutEnv } from '../../drawer/env_context.js';
import type { CheckoutBusCopy } from '../../model/checkout_copy.js';
import type { BusItemSource } from '../../model/checkout_source.js';
import './item_detail.css';

type BusItemDetail = BusItemSource['detail'];

export interface BusItemDetailProps {
  detail: BusItemDetail;
  price: { amount: number; currency: string };
}

/** "3h20min" / "45min" a partir dos minutos do provider (espelha `bus_offers`). */
export function fmtDurationMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const min = total % 60;
  if (h && min) return `${String(h)}h${String(min)}min`;
  if (h) return `${String(h)}h`;
  return `${String(min)}min`;
}

/** Cabeçalho do trecho: ícone + viação (· classe de poltrona quando presente). */
function BusHead({ detail }: { readonly detail: BusItemDetail }) {
  return (
    <div className="pcc-bus__head">
      <BusIcon size={16} aria-hidden />
      {detail.carrierName && <span className="pcc-bus__carrier">{detail.carrierName}</span>}
      {detail.seatClass && <span className="pcc-bus__class">· {detail.seatClass}</span>}
    </div>
  );
}

/** Trecho dep → chegada: horário + rodoviária nas pontas, direto/conexões +
 *  duração no meio. Núcleo compartilhado pelo compacto e pelo expandido. */
function BusTrip({ detail }: { readonly detail: BusItemDetail }) {
  const copy = useCheckoutEnv().copy.bus;
  const connections = detail.connections ?? 0;
  const direct = connections <= 0;
  return (
    <div className="pcc-bus__trip">
      <div className="pcc-bus__pt">
        <span className="pcc-bus__time">{fmtTime(detail.departure.dateTime)}</span>
        <span className="pcc-bus__stop">{detail.departure.stopName}</span>
      </div>
      <div className="pcc-bus__conn">
        <span className="pcc-bus__stops">
          {direct ? copy.direct : copy.connections(connections)}
        </span>
        <span className="pcc-bus__line" aria-hidden="true" />
        {detail.durationMinutes != null && (
          <span className="pcc-bus__dur">{fmtDurationMinutes(detail.durationMinutes)}</span>
        )}
      </div>
      <div className="pcc-bus__pt pcc-bus__pt--end">
        <span className="pcc-bus__time">{fmtTime(detail.arrival.dateTime)}</span>
        <span className="pcc-bus__stop">{detail.arrival.stopName}</span>
      </div>
    </div>
  );
}

/** As poltronas para a linha do resumo: um trecho só mostra os códigos; com
 *  conexão, cada trecho com o seu rótulo. Trecho sem poltrona = sem assento. */
export function seatsText(seats: readonly (readonly string[])[], copy: CheckoutBusCopy): string {
  const text = (codes: readonly string[]) => (codes.length > 0 ? codes.join(', ') : copy.noSeat);
  const [only] = seats;
  if (seats.length === 1 && only) return text(only);
  return seats.map((codes, index) => `${copy.segment(index + 1)}: ${text(codes)}`).join(' · ');
}

/** Linha chave/valor do resumo da reserva (`.co-resv__*`): classe, poltronas. */
function ResvRow({ k, v }: { readonly k: string; readonly v: string }) {
  return (
    <div className="co-resv__row">
      <span className="co-resv__row-k">{k}</span>
      <span className="co-resv__row-v">{v}</span>
    </div>
  );
}

/** Expanded variant — bus summary inside the unified checkout DRAWER ("Sua
 *  reserva" panel). The leg and day ("Ida · Qui, 22 out" in a round trip), the
 *  compact trip block + the shared `ResvRow` key/value rows (seat class and the
 *  chosen seats). Total/CTA stay on the drawer's generic price panel. */
export function BusItemDetailExpanded({ detail, price }: Readonly<BusItemDetailProps>) {
  const { copy: text, locale } = useCheckoutEnv();
  const copy = text.bus;
  const date = fmtWeekdayDate(detail.departure.dateTime, locale);
  const day = detail.leg && date ? `${copy.leg[detail.leg]} · ${date}` : date;
  return (
    <div className="pcc-bus">
      <div className="pcc-bus__main">
        {day && <p className="pcc-bus__date">{day}</p>}
        <BusHead detail={detail} />
        <BusTrip detail={detail} />
        <span className="pcc-bus__sr-price">
          {price.amount} {price.currency}
        </span>
      </div>
      {detail.seatClass && <ResvRow k={copy.fareClass} v={detail.seatClass} />}
      {detail.seats && detail.seats.length > 0 && (
        <ResvRow k={copy.seats} v={seatsText(detail.seats, copy)} />
      )}
    </div>
  );
}
